import React, {
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import classNames from 'classnames';
import Cropper from 'cropperjs';
import { useDispatch, useSelector } from 'react-redux';
import { GreenroomState as GS } from '../../../stores/types/types';
import * as ModalActions from '../../../stores/actions/modal';
import * as SessionActions from '../../../stores/actions/session';
import { UploadImage, UploadProfileImage, UploadResponse } from '../../../connection/upload-image';
import { GetCloudfrontUploadUrl, UploadToCloudFront } from '../../../connection/media';
import { AWS_ALIAS } from '../../../types/constants';
import { random } from '../../../utils/random';
import Icon, { COLORS, ICONS } from '../../general-components/icon';
import { MODAL_AVATAR_ACTIONS, MODAL_SAFETY_PHOTO_ACTIONS } from '../../../stores/reducers/modal';
import { createMediaFile } from '../../../utils/media';
import { cleanupVideoStreamElement } from '../../../utils/video/video-streams';
import { IMG_TYPES } from '../../../types/constants';
import { setProfileInfo, updateProfile } from '../../../stores/actions/session';
import { showAlert } from '../../general-components/alert-service';
import { VirtualBg } from '../../../stores/types/content';
import useAddBackground from '../../../hooks/virtual-backgrounds/use-add-background';
import { GreenroomUser } from '../../../stores/reducers/session';
import { ConnectedUser } from '../../../connection/socket';
import useEditProfileMask from '../../../hooks/use-edit-profile-mask';
import useProfile from '../../../hooks/use-profile';
import { UpdateOnsiteLocation, UpdateSrtStream } from '../../../connection/video';

const AVATAR_PHOTO_STATES = {
	UPLOAD: 'upload',
	EDIT: 'edit',
	CAMERA: 'camera',
};

interface Props {
	isOnboarding: boolean;
	modalAvatarAction: string;
}

export default function AvatarPhoto(props: Props) {
	const dispatch = useDispatch();
	const token = useSelector((s: GS) => s.SessionReducer.token);
	const localUser = useSelector((s: GS) => s.SessionReducer.user);
	const host = useSelector((s: GS) => s.SessionReducer.host);
	const meeting = useSelector((s: GS) => s.SessionReducer.meeting);
	const mainVideoStream = useSelector((s: GS) => s.VideoReducer.mainVideoStream);
	const selectedChannel = useSelector((s: GS) => s.HomeReducer.selectedChannel);

	const {
		existingInfo,
		displayedProfile,
		srtStreamId,
		onsiteLocationId,
		uploadCallback,
	} = useSelector((state: GS) => state.ModalReducer.modalData);

	const {
		isOnboarding,
		modalAvatarAction,
	} = props;

	const user = displayedProfile ?? localUser as GreenroomUser | ConnectedUser;

	const fileInput = useRef<HTMLInputElement | null>(null);
	const imageElement = useRef<HTMLImageElement>(null);
	const cropper = useRef<Cropper | null>(null);
	const videoElement = useRef<HTMLVideoElement | null>(null);

	const [avatarPhotoState, setAvatarPhotoState] = useState<string>(AVATAR_PHOTO_STATES.UPLOAD);
	const [originalImage, setOriginalImage] = useState<string>('');
	const [originalImageName, setOriginalImageName] = useState<string>('');
	const [originalImageType, setOriginalImageType] = useState<string>('');
	const [zoomRatio, setZoomRatio] = useState<number>(1);
	const [minSliderZoom, setMinSliderZoom] = useState<number>(0);
	const [maxSliderZoom, setMaxSliderZoom] = useState<number>(1);
	const [isDraggingOver, setIsDraggingOver] = useState<boolean>(false);
	const [isLoading, setIsLoading] = useState<boolean>(false);

	const { uploadNewVBG } = useAddBackground();

	const { watchedProfile, watchedIsMasked } = useProfile({ suggestedProfile: user?.gr_user });
	const { saveProfileMask } = useEditProfileMask({ watchedProfile });

	function setEditorInfo(url: string, name: string, type: string) {
		setIsLoading(true);
		setOriginalImage(url);
		setOriginalImageName(name);
		setOriginalImageType(type);
		setAvatarPhotoState(AVATAR_PHOTO_STATES.EDIT);
	}

	function setEditorState(file: File) {
		setIsLoading(true);
		const url = URL.createObjectURL(file);
		setEditorInfo(url, file.name.split('.')[0], file.type);
	}

	useEffect(() => {
		if (imageElement.current && avatarPhotoState === AVATAR_PHOTO_STATES.EDIT) {
			const aspectRatio = modalAvatarAction === MODAL_AVATAR_ACTIONS.AVATAR ? 1 : (16 / 9);
			cropper.current = new Cropper(
				imageElement.current,
				{
					aspectRatio,
					guides: false,
					center: false,
					responsive: false,
					rotatable: false,
					zoomOnTouch: false,
					highlight: false,
					cropBoxResizable: false,
					cropBoxMovable: false,
					viewMode: 0,
					dragMode: 'move',
					autoCropArea: 0,
					ready: () => {
						if (!cropper.current) return;

						const cropperTop = document.getElementsByClassName('cropper-point point-n');
						const helperDiv = document.getElementById('crop-box-helper');
						if (cropperTop[0] && helperDiv) cropperTop[0].appendChild(helperDiv);

						const imageData = cropper.current.getImageData();

						const containingBoxWidth = 560;
						const containingBoxHeight = 316;
						const padding = 32;
						const width = (containingBoxWidth - padding * 2);
						const height = (9 / 16) * width;

						const minWidth = width / imageData.naturalWidth;
						const minHeight = height / imageData.naturalHeight;
						const minZoom = minHeight < minWidth ? minHeight : minWidth;
						const maxZoom = minZoom * 3;

						const avatarCropBox = {
							left: padding + (containingBoxWidth - containingBoxHeight) / 2,
							top: padding,
							width: (containingBoxHeight - padding * 2),
							height: (containingBoxHeight - padding * 2),
						};

						const sixteenNine = {
							width,
							height,
							top: (containingBoxHeight - height) / 2,
							left: padding,
						};

						const cropBox = modalAvatarAction === MODAL_AVATAR_ACTIONS.AVATAR
							? avatarCropBox : sixteenNine;

						cropper.current.setCropBoxData(cropBox);
						setMinSliderZoom(minZoom);
						setMaxSliderZoom(maxZoom);

						setZoomRatio(minZoom);

						const startZoom = Math.max(minHeight, minWidth);
						cropper.current.zoomTo(startZoom);

						setIsLoading(false);
					},
					zoom: (event: CustomEvent) => {
						if (cropper.current) {
							if (event.detail.ratio > 10) {
								event.preventDefault();
								cropper.current.zoomTo(10);
								setZoomRatio(10);
							} else {
								setZoomRatio(event.detail.ratio);
							}
						}
					},
				},
			);
		}
	}, [avatarPhotoState]);

	function zoomWithSlider(event: React.ChangeEvent<HTMLInputElement>) {
		if (!cropper.current) return;

		if (event.target.valueAsNumber) {
			cropper.current.zoomTo(event.target.valueAsNumber);
		}
	}

	function checkFileInput(event: React.ChangeEvent<HTMLInputElement>) {
		if (event.target.files) {
			setEditorState(event.target.files[0]);
		}
	}

	function checkFileDrop(event: React.DragEvent<HTMLInputElement>) {
		event.preventDefault();
		setIsDraggingOver(false);
		const file = event.dataTransfer.files[0];
		const acceptedTypes = ['image/png', 'image/jpg', 'image/jpeg'];
		if (acceptedTypes?.includes(file.type)) {
			setEditorState(file);
		}
	}

	async function uploadLogo(channelId: string | number, file: File) {
		const fileToUpload = createMediaFile(file);
		const prefix = `uploads/greenroom-channels/${channelId}`;
		const key = `${prefix}/${fileToUpload.name}`;
		const { url: signedUrl } = await GetCloudfrontUploadUrl(Number(channelId), key);
		await UploadToCloudFront(signedUrl, fileToUpload);
		return `${AWS_ALIAS}/${key}`;
	}

	function backgroundImageChange(file: File) {
		if (file) {
			UploadImage(token, file, true).then((res: UploadResponse) => {
				if (res.filename && token) {
					const {
						SAFETY_PHOTO,
						SAFETY_PHOTO_SRT,
						SAFETY_PHOTO_ONSITE_LOCATION,
					} = MODAL_SAFETY_PHOTO_ACTIONS;

					if (modalAvatarAction === SAFETY_PHOTO && user) {
						const columns = { bg_image: res.url, gr_user: user.gr_user };
						if (localUser?.gr_user === user.gr_user && !watchedIsMasked) {
							dispatch(setProfileInfo({ bg_image: res.url }));
							dispatch(updateProfile(user.gr_user, columns, token));
						} else {
							saveProfileMask({ ...user, ...columns });
						}
					}
					if (modalAvatarAction === SAFETY_PHOTO_SRT && meeting) {
						UpdateSrtStream(
							token,
							meeting.meeting,
							srtStreamId,
							{ safetyImage: res.url },
						);
					}
					if (modalAvatarAction === SAFETY_PHOTO_ONSITE_LOCATION && meeting) {
						UpdateOnsiteLocation(
							token,
							meeting.meeting,
							onsiteLocationId,
							{ safetyImage: res.url },
						);
					}
				}
			}).catch((e) => {
				console.error(e);
				showAlert({
					message: 'Error uploading image',
					description: 'We ran into an issue uploading that image. Please try again.',
					duration: 4000,
				});
			});
		}
	}

	const [saving, setSaving] = useState(false);
	function uploadEditedImage() {
		if (!cropper.current || !user || !token || saving) return;
		setSaving(true);
		const canvas = cropper.current.getCroppedCanvas();
		canvas.toBlob((blob) => {
			const nameType = originalImageType.indexOf('/') > -1 ? originalImageType.split('/')[1] : originalImageType;
			const file = new File([blob as Blob], `${random(6)}${originalImageName}.${nameType}`, { type: originalImageType });
			if (modalAvatarAction === MODAL_AVATAR_ACTIONS.AVATAR) {
				UploadProfileImage(token, file).then((res: UploadResponse) => {
					const columns = { avatar: res.url, gr_user: user.gr_user };
					if (user.gr_user === localUser?.gr_user && !watchedIsMasked) {
						dispatch(SessionActions.setProfileInfo({ avatar: res.url }));
						dispatch(SessionActions.updateProfile(user.gr_user, columns, token));
					} else {
						saveProfileMask({ ...user, ...columns });
					}
				});
			} else if (modalAvatarAction === MODAL_AVATAR_ACTIONS.CHANNEL_ICON && selectedChannel) {
				uploadLogo(selectedChannel.channel, file).then((res: string) => {
					dispatch(SessionActions.updateChannel(token, { ...selectedChannel, logo_white: res }));
				});
			} else if (
				modalAvatarAction === MODAL_SAFETY_PHOTO_ACTIONS.SAFETY_PHOTO
				|| modalAvatarAction === MODAL_SAFETY_PHOTO_ACTIONS.SAFETY_PHOTO_SRT
				|| modalAvatarAction === MODAL_SAFETY_PHOTO_ACTIONS.SAFETY_PHOTO_ONSITE_LOCATION
			) {
				backgroundImageChange(file);
			} else if (modalAvatarAction === MODAL_AVATAR_ACTIONS.VIRTUAL_BACKGROUND) {
				uploadNewVBG(file);
			} else if (uploadCallback && modalAvatarAction === MODAL_AVATAR_ACTIONS.HOLD_SLIDE) {
				uploadCallback(file);
			}
			dispatch(ModalActions.setModalClosed());
		}, originalImageType);
	}

	function grayButtonClick() {
		if (avatarPhotoState === AVATAR_PHOTO_STATES.CAMERA) {
			setAvatarPhotoState(AVATAR_PHOTO_STATES.UPLOAD);
		} else if (
			modalAvatarAction === MODAL_AVATAR_ACTIONS.VIRTUAL_BACKGROUND
			|| modalAvatarAction === MODAL_AVATAR_ACTIONS.HOLD_SLIDE
		) {
			dispatch(ModalActions.setModalClosed());
		} else {
			setAvatarPhotoState(AVATAR_PHOTO_STATES.CAMERA);
		}
	}

	function greenButtonClick() {
		if (avatarPhotoState === AVATAR_PHOTO_STATES.CAMERA) {
			if (!videoElement.current) return;

			setIsLoading(true);
			const canvas = document.createElement('canvas');
			const ctx = canvas.getContext('2d');

			canvas.width = videoElement.current?.videoWidth;
			canvas.height = videoElement.current?.videoHeight;

			if (isOnboarding || !host) {
				ctx?.translate(videoElement.current?.videoWidth, 0);
				ctx?.scale(-1, 1);
			}

			ctx?.drawImage(
				videoElement.current,
				0,
				0,
				videoElement.current?.videoWidth,
				videoElement.current?.videoHeight,
			);

			const frame = canvas.toDataURL('image/png');
			setOriginalImage(frame);
			setOriginalImageName(`camera_capture${random(10)}`);
			setOriginalImageType('image/png');
			setAvatarPhotoState(AVATAR_PHOTO_STATES.EDIT);
			canvas.remove();
		} else {
			uploadEditedImage();
		}
	}

	const header = useMemo(() => {
		switch (modalAvatarAction) {
			case MODAL_AVATAR_ACTIONS.AVATAR:
				return 'Avatar';
			case MODAL_AVATAR_ACTIONS.VIRTUAL_BACKGROUND:
				return 'Background photo';
			case MODAL_AVATAR_ACTIONS.HOLD_SLIDE:
				return 'Hold slide';
			default:
				return 'Safety photo';
		}
	}, [modalAvatarAction]);

	const actionText = useMemo(() => {
		switch (modalAvatarAction) {
			case MODAL_SAFETY_PHOTO_ACTIONS.SAFETY_PHOTO:
				return 'safety photo';
			case MODAL_SAFETY_PHOTO_ACTIONS.SAFETY_PHOTO_SRT:
				return 'safety photo';
			case MODAL_SAFETY_PHOTO_ACTIONS.SAFETY_PHOTO_ONSITE_LOCATION:
				return 'safety photo';
			case MODAL_AVATAR_ACTIONS.AVATAR:
				return 'avatar';
			case MODAL_AVATAR_ACTIONS.VIRTUAL_BACKGROUND:
				return 'background';
			case MODAL_AVATAR_ACTIONS.HOLD_SLIDE:
				return 'hold slide';
			case MODAL_AVATAR_ACTIONS.CHANNEL_ICON:
				return 'channel icon';
			default:
				return '';
		}
	}, [modalAvatarAction]);

	const avatarHeaderText = useMemo(() => {
		switch (avatarPhotoState) {
			case AVATAR_PHOTO_STATES.UPLOAD:
				return `Edit ${actionText}`;
			case AVATAR_PHOTO_STATES.EDIT:
				return header;
			case AVATAR_PHOTO_STATES.CAMERA:
				return 'Take a photo';
			default:
				return '';
		}
	}, [avatarPhotoState, actionText]);

	useEffect(() => {
		const typedInfo = existingInfo as VirtualBg;
		if (existingInfo && typedInfo.name && typedInfo.fileType) {
			setEditorInfo(typedInfo.url, typedInfo.name, typedInfo.fileType);
		}
	}, [existingInfo]);

	return (
		<div className="info-modal avatar-photo">
			<div className="info-modal-header align-items-center justify-content-space-between">
				<h4 className='bl-font-header' style={{ marginBottom: 0 }}>{avatarHeaderText}</h4>
				<button
					style={{ padding: '8px', right: '-8px', position: 'relative' }}
					onClick={() => dispatch(ModalActions.setModalOpen(false))}>
					<Icon name={ICONS.CLOSE} color={COLORS.TEXT_GRAY_400} size={16} />
				</button>
			</div>
			<div className="avatar-photo-modal-body width-full">
				{avatarPhotoState === AVATAR_PHOTO_STATES.UPLOAD && (
					<div className={classNames('upload-container', isDraggingOver && 'dragging-over')}
						onDragOver={(event: React.DragEvent<HTMLInputElement>) => event.preventDefault()}
						onDragEnter={(event: React.DragEvent<HTMLInputElement>) => {
							event.preventDefault();
							setIsDraggingOver(true);
						}}
						onDragLeave={(event: React.DragEvent<HTMLInputElement>) => {
							event.preventDefault();
							setIsDraggingOver(false);
						}}
						onDrop={checkFileDrop}
					>
						<button className="round padding-small background-lighter" onClick={() => fileInput.current?.click()}>
							<Icon name={ICONS.ADD} color={COLORS.WHITE} size={10} />
						</button>
						<span className="font-weight-normal bl-font-p1">Drag & Drop to add your photo</span>
					</div>
				)}
				{avatarPhotoState === AVATAR_PHOTO_STATES.EDIT && (<>
						<div className="main-container">
							<img style={{ opacity: isLoading ? 0 : 1 }}
								ref={imageElement}
								src={originalImage}
								alt="Source" />
							<div id="crop-box-helper">
								<Icon name={ICONS.MOVE} color={COLORS.WHITE} size={12} />
								<span>Drag to Reposition</span>
							</div>
						</div>

					</>
				)}
				{avatarPhotoState === AVATAR_PHOTO_STATES.CAMERA && (
					<div className="main-container">
						<video
							autoPlay
							playsInline
							style={{ transform: 'scaleX(-1)' }}
							ref={(ref) => {
								if (ref && ref.srcObject !== mainVideoStream) {
									try {
										ref.srcObject = mainVideoStream; // eslint-disable-line no-param-reassign
										ref.volume = 0; // eslint-disable-line no-param-reassign
										ref.play().catch((e) => console.error(e));
										videoElement.current = ref;
										cleanupVideoStreamElement(ref);
									} catch (e) {
										console.error(e);
									}
								}
							}}>
						</video>
					</div>
				)}

				<div className="crop-slider">
					<Icon name={ICONS.ZOOM_OUT} color={COLORS.MEDIUM_GRAY} size={16} />
					<input
						type="range"
						onChange={zoomWithSlider}
						value={zoomRatio}
						step={(maxSliderZoom - minSliderZoom) / 100}
						min={minSliderZoom}
						max={maxSliderZoom}
						disabled={avatarPhotoState !== AVATAR_PHOTO_STATES.EDIT}
					/>
					<Icon name={ICONS.ZOOM_IN} color={COLORS.MEDIUM_GRAY} size={16} />
				</div>
			</div>
			<div className="info-modal-bottom">
				<div className="info-modal-buttons">
					{(modalAvatarAction === MODAL_AVATAR_ACTIONS.AVATAR
					|| modalAvatarAction === MODAL_SAFETY_PHOTO_ACTIONS.SAFETY_PHOTO
					|| modalAvatarAction === MODAL_SAFETY_PHOTO_ACTIONS.SAFETY_PHOTO_SRT
					|| modalAvatarAction === MODAL_SAFETY_PHOTO_ACTIONS.SAFETY_PHOTO_ONSITE_LOCATION
					|| modalAvatarAction === MODAL_AVATAR_ACTIONS.VIRTUAL_BACKGROUND
					|| modalAvatarAction === MODAL_AVATAR_ACTIONS.HOLD_SLIDE) && (
						<button
							className={classNames('info-gray bl-font-l1', avatarPhotoState === AVATAR_PHOTO_STATES.UPLOAD && 'upload')}
							onClick={grayButtonClick}
							disabled={!mainVideoStream}
						>
							{avatarPhotoState === AVATAR_PHOTO_STATES.CAMERA
								|| modalAvatarAction === MODAL_AVATAR_ACTIONS.VIRTUAL_BACKGROUND
								|| modalAvatarAction === MODAL_AVATAR_ACTIONS.HOLD_SLIDE ? 'Cancel' : 'Use camera'}
						</button>
					)}
					{avatarPhotoState !== AVATAR_PHOTO_STATES.UPLOAD && (
						<button className="info-gradient bl-font-l1" onClick={greenButtonClick} disabled={false}>
							{avatarPhotoState === AVATAR_PHOTO_STATES.CAMERA ? 'Take photo' : `Set ${actionText}`}
						</button>
					)}
				</div>
				{avatarPhotoState === AVATAR_PHOTO_STATES.EDIT && (
					<button className="transparent height-full align-items-center margin-left-small info-extra" style={{ height: '48px' }}
						onClick={() => {
							if (fileInput.current) fileInput.current.value = '';
							setAvatarPhotoState(AVATAR_PHOTO_STATES.UPLOAD);
							setOriginalImage('');
							fileInput.current?.click();
						}}>
						<Icon name={ICONS.UPLOAD} color={COLORS.WHITE} size={16} />
						<span className="text-white font-weight-heavy margin-left-small bl-font-l1">Replace photo</span>
					</button>
				)}
			</div>
			<input
				accept={IMG_TYPES.map((a) => `.${a}`).join(',')}
				onChange={checkFileInput}
				ref={(ref) => { fileInput.current = ref; }}
				type="file"
				style={{ display: 'none' }}
			/>
		</div>
	);
}
